import React, { FC, useEffect, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useAccountContext } from 'application/providers/AccountProvider';
import AnalyticsService, {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import usePlayerChangeController from 'application/hooks/usePlayerChangeController';
import { useOnboardingActionContext } from 'application/components/common';

import { DeviceTypes } from 'domain/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconSettings from '@zvuk-b2b/react-uikit/ui/icons/Settings';
import IconMic from '@zvuk-b2b/react-uikit/ui/icons/Mic';
import IconVolume2 from '@zvuk-b2b/react-uikit/ui/icons/Volume2';
import Popper, { HintPlacement } from '@zvuk-b2b/react-uikit/ui/Popper';

import { TEXTS } from './texts';
import useRequestDevicesController from '../useRequestDevicesController';
import { useOnboardingContext } from '../../useOnboardingContext';
import { DeviceTypesInFlow, PlayerTypesInFlow, SelectPlayerFlowType } from '../../types';

import styles from '../index.module.scss';

const DEVICE_IMAGE_PATH = '/assets/img/device_photos/sberBoom.png';

const SberBoomModal: FC<ModalProps[ModalTypes.SBERBOOM_REQUEST]> = ({ close, openModal, replaceModal }) => {
  const { flowType, currentPoint } = useOnboardingContext();
  const { clearCurrentPoint } = useOnboardingActionContext();

  const { deviceRequest, deviceRequestLoading, currentCompany, userRequest } = useAccountContext();
  const { onSuccessCallback, hasRecentRequest, cantRequestDevice, isFirstRequest } = useRequestDevicesController({
    point: currentPoint,
    deviceRequests: currentCompany?.deviceRequests,
    modalDeviceType: DeviceTypesInFlow.BOOM_SPEAKER,
  });

  const { setToPlayerType } = usePlayerChangeController({
    pointId: currentPoint?.id || '',
    fromPlayerType: DeviceTypes.BoomSpeaker,
    onSuccessCallback,
  });

  useEffect(() => {
    setToPlayerType('WEB');
  }, []);

  const onSubmit = async () => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_REQUEST_DEVICE_CLICKED, {
      [StreamBindFlowParamNameAMP.DEVICE_TYPE]: PlayerTypesInFlow.BOOM_SPEAKER,
      [StreamBindFlowParamNameAMP.FIRST_DEVICE_REQUEST]: isFirstRequest,
    });

    const result = await deviceRequest({
      companyId: currentCompany?.id || '',
      deviceTypes: [DeviceTypes.BoomSpeaker],
      pointId: currentPoint?.id || '',
    });

    if (result.ok) {
      replaceModal(ModalTypes.DEVICE_REQUESTED, {});
      clearCurrentPoint();
      userRequest();
    }
  };

  const onConfirmClose = () => {
    if (flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  const handleCancel = () => {
    if (hasRecentRequest) {
      clearCurrentPoint();
      close();
    } else {
      onConfirmClose();
    }
  };

  const SubmitButton = (
    <Button
      key="button_sberboom_request_modal_success"
      elementId="button_sberboom_request_modal_success"
      type={ButtonType.PRIMARY}
      onClick={onSubmit}
      isLoading={deviceRequestLoading}
      isDisabled={hasRecentRequest || cantRequestDevice}>
      {hasRecentRequest ? TEXTS.SUCCESS_DISABLED_BUTTON : TEXTS.SUCCESS_BUTTON}
    </Button>
  );

  const CancelButton = (
    <Button
      key="button_sberboom_request_modal_cancel"
      elementId="button_sberboom_request_modal_cancel"
      type={ButtonType.SECONDARY}
      isLoading={deviceRequestLoading}
      onClick={() => {
        replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
      }}>
      {TEXTS.CANCEL_BUTTON}
    </Button>
  );

  const SubmitButtonWithHint = (
    <Popper
      containerClassName={styles.DeviceModalHint}
      placement={HintPlacement.PLACEMENT_TOP_START}
      triggerNode={SubmitButton}
      isShow>
      {TEXTS.DISABLED_BUTTON_BINDED_HINT}
    </Popper>
  );

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      className={styles.DeviceModal}
      contentClassName={styles.DeviceModalContent}
      containerClassName={styles.DeviceModalContainer}
      actionButtons={[cantRequestDevice ? SubmitButtonWithHint : SubmitButton, CancelButton]}>
      <img
        src={DEVICE_IMAGE_PATH}
        alt=""
        loading="lazy"
        className={styles.DeviceModalImage}
      />

      <h2>{TEXTS.MODAL_TITLE}</h2>
      <div>{TEXTS.TAGLINE}</div>

      <ul>
        <li>
          <IconMic />
          {TEXTS.FEATURES_VOICE}
        </li>
        <li>
          <IconSettings />
          {TEXTS.FEATURES_SMART_HOUSE}
        </li>
        <li>
          <IconVolume2 />
          {TEXTS.FEATURES_NOISE}
        </li>
      </ul>

      <div>{TEXTS.DESCRIPTION_FIRST_BLOCK}</div>
      <div>
        {TEXTS.DESCRIPTION_SECOND_BLOCK}
        <br />
        {TEXTS.DESCRIPTION_THIRD_BLOCK}
      </div>
    </ModalScreen>
  );
};

export default memo(SberBoomModal);
